import { useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface SegmentEvent {
  id: number
  name: string
  status: string
  date?: string | null
}

export function SegmentEventPicker({
  events,
  selected,
  loading,
  error,
  onChange,
}: {
  events: SegmentEvent[]
  selected: number[]
  loading: boolean
  error: boolean
  onChange: (selected: number[]) => void
}) {
  const [open, setOpen] = useState(false)
  const chosen = selected.flatMap((id) => {
    const event = events.find((item) => item.id === id)
    return event ? [event] : []
  })
  const toggle = (id: number) =>
    onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id])
  const unavailable = selected.length - chosen.length
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label="Events"
          className="col-span-2 h-auto min-h-9 w-full min-w-0 justify-start gap-1.5 bg-popover font-normal text-body-sm sm:w-auto sm:flex-1"
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">Choose events</span>
          ) : (
            <span className="flex min-w-0 flex-wrap gap-1.5">
              {chosen.slice(0, 2).map((event) => (
                <Badge key={event.id} variant="neutral" className="min-w-0 gap-1.5">
                  <span className="truncate">{event.name}</span>
                  {event.status === 'cancelled' && (
                    <span className="text-muted-foreground">Cancelled</span>
                  )}
                </Badge>
              ))}
              {chosen.length > 2 && <Badge variant="neutral">+{chosen.length - 2}</Badge>}
              {unavailable > 0 && (
                <span className="text-caption text-muted-foreground">
                  {unavailable} unavailable {unavailable === 1 ? 'event' : 'events'}
                </span>
              )}
            </span>
          )}
          <ChevronDown
            className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <Command>
          <CommandInput placeholder="Search events" />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Loading events…' : error ? 'Events unavailable' : 'No events found'}
            </CommandEmpty>
            <CommandGroup>
              {events.map((event) => (
                <CommandItem
                  key={event.id}
                  value={String(event.id)}
                  keywords={[event.name, event.date ?? '']}
                  onSelect={() => toggle(event.id)}
                  aria-checked={selected.includes(event.id)}
                >
                  <Check
                    className={cn(
                      'h-4 w-4 shrink-0 text-primary',
                      !selected.includes(event.id) && 'invisible',
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate">{event.name}</span>
                  {event.status === 'cancelled' && <Badge variant="neutral">Cancelled</Badge>}
                  <span className="ml-auto shrink-0 text-caption text-muted-foreground">
                    {event.date}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
